'use client';

export type NotificationItem = {
  id: string;
  type: 'video_upload' | 'new_product' | 'messages' | 'jobs' | 'orders' | 'wallet';
  title: string;
  body: string;
  time: string;
  read: boolean;
  icon: string; // emoji icon key
  color: string;
  createdAt: number;
};

type Listener = () => void;

// Simple in-memory store with pub/sub for cross-component reactivity
class NotificationStore {
  private items: NotificationItem[] = [
    {
      id: 'n1', type: 'messages', title: 'New message from Amina Hassan',
      body: 'Hey! Is the Samsung Galaxy still available?',
      time: '2 min ago', read: false, icon: 'message', color: '#00C853', createdAt: Date.now() - 120000,
    },
    {
      id: 'n2', type: 'jobs', title: 'Job Application Update',
      body: 'Safaricom PLC has reviewed your application for Senior Engineer',
      time: '15 min ago', read: false, icon: 'briefcase', color: '#FFB300', createdAt: Date.now() - 900000,
    },
    {
      id: 'n3', type: 'wallet', title: 'Wallet Credited',
      body: 'KES 500 has been added to your wallet. Balance: KES 4,250',
      time: '1h ago', read: false, icon: 'wallet', color: '#00C853', createdAt: Date.now() - 3600000,
    },
    {
      id: 'n4', type: 'orders', title: 'Order Confirmed',
      body: 'Your order from Mama Oliech Restaurant has been confirmed',
      time: '2h ago', read: true, icon: 'package', color: '#76FF03', createdAt: Date.now() - 7200000,
    },
    {
      id: 'n5', type: 'orders', title: 'Review Request',
      body: 'How was your stay at Serena Hotel? Leave a review.',
      time: '1d ago', read: true, icon: 'star', color: '#FFB300', createdAt: Date.now() - 86400000,
    },
  ];

  private listeners: Set<Listener> = new Set();

  subscribe(fn: Listener) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private notify() {
    this.listeners.forEach(fn => fn());
  }

  getAll(): NotificationItem[] {
    return [...this.items].sort((a, b) => b.createdAt - a.createdAt);
  }

  getUnreadCount(): number {
    return this.items.filter(n => !n.read).length;
  }

  markRead(id: string) {
    this.items = this.items.map(n => n.id === id ? { ...n, read: true } : n);
    this.notify();
  }

  markAllRead() {
    this.items = this.items.map(n => ({ ...n, read: true }));
    this.notify();
  }

  addNotification(notif: Omit<NotificationItem, 'id' | 'createdAt' | 'read'>) {
    const newItem: NotificationItem = {
      ...notif,
      id: `n-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      read: false,
      createdAt: Date.now(),
    };
    this.items = [newItem, ...this.items];
    this.notify();
    // Browser push notification (if permission granted)
    this.sendBrowserPush(newItem.title, newItem.body);
  }

  private sendBrowserPush(title: string, body: string) {
    if (typeof window === 'undefined') return;
    if (!('Notification' in window)) return;
    if (Notification.permission === 'granted') {
      try {
        new Notification(title, {
          body,
          icon: '/assets/images/app_logo.png',
          badge: '/assets/images/app_logo.png',
        });
      } catch {
        // silently fail if notification blocked
      }
    }
  }

  requestPushPermission() {
    if (typeof window === 'undefined') return;
    if (!('Notification' in window)) return;
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }

  // Helpers for specific events
  notifyVideoUploaded(creatorName: string, videoTitle: string) {
    this.addNotification({
      type: 'video_upload',
      title: `📹 New video by ${creatorName}`,
      body: `"${videoTitle}" was just uploaded. Watch now!`,
      time: 'Just now',
      icon: 'video',
      color: '#FF3B5C',
    });
  }

  notifyNewProduct(storeName: string, productName: string, module: string) {
    this.addNotification({
      type: 'new_product',
      title: `🛍️ New listing on ${module}`,
      body: `${storeName} added "${productName}" — check it out!`,
      time: 'Just now',
      icon: 'product',
      color: '#00C853',
    });
  }
}

// Singleton
export const notificationStore = new NotificationStore();
